import { format } from 'date-fns'
import { t } from 'i18next'
import React from 'react'
import { Trans } from 'react-i18next'

import {
    ClaimDamageLocation,
    ClaimDamageType,
    ClaimProductResolution,
    ClaimStatus,
    CustomerStatus,
    PlanCode,
    PolicyProvider,
    SubscriptionStatus,
} from '../../../common/enums.js'
import { formatMonthlyPrice, formatPrice } from '../../../common/utils/utils.js'
import { AccordionItemProps } from '../../components/accordion/accordion.js'
import type { AlertItemProps } from '../../components/alert/alert.js'
import { ButtonProps } from '../../components/button/button.js'
import { FooterColumnProps, FooterProps } from '../../components/footer/footer.js'
import { CheckCardProps } from '../../components/forms/check-card/check-card.js'
import { CheckboxProps } from '../../components/forms/checkbox/checkbox.js'
import { ChooseMoreProps } from '../../components/forms/choose-more/choose-more.js'
import { DatepickerProps } from '../../components/forms/datepicker/datepicker.js'
import { FileUpload } from '../../components/forms/file-upload/file-upload.js'
import { SelectProps } from '../../components/forms/select/select.js'
import { Textarea, TextareaProps } from '../../components/forms/textarea/textarea.js'
import { HeaderFlexItemProps, HeaderFlexProps } from '../../components/header-flex/header-flex.js'
import {
    AccountIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    ContactIcon,
    DamageAudioIcon,
    DamageBackIcon,
    DamageCameraIcon,
    DamageFrontIcon,
    DamageNetworkIcon,
    DamagePowerIcon,
    InvoicesIcon,
    SubscriptionsIcon,
    WalletIcon,
} from '../../components/icon/icon.js'
import { ModalContent, ModalHeader } from '../../components/modal/modal.js'
import { NotificationCardProps } from '../../components/notification-card/notification-card.js'
import { NotificationProps } from '../../components/notification/notification.js'
import { PaymentCardProps } from '../../components/payment-card/payment-card.js'
import { SubscriptionCardProps } from '../../components/subscription-card/subscription-card.js'
import { Customer } from '../../models/customer.js'
import { Subscription } from '../../models/subscription.js'
import { RootProps } from '../../root.js'
import { type PortalAccountView, PortalViewPaths, Route, View } from '../../types.js'
import { getExpiringPaymentMethodDueDate, isPaymentMethodExpiring } from '../../utils/payment-method-utils.js'
import { formatLocaleForEsisTag, getTranslatedErrorMessage } from '../../utils/utils.js'
import { NotificationPage, NotificationPageProps } from '../notification/notification.js'
import { ViewAccount, ViewAccountBlockProps, ViewAccountProps } from './account/account.js'
import { ClaimDamageProps, UpsellOptionType, UpsellProps, ViewCancel, ViewCancelProps } from './cancel/cancel.js'
import { Dashboard, DashboardProps } from './dashboard/dashboard.js'
import { Debug, DebugProps } from './debug/debug.js'
import { ViewFaq, ViewFaqProps } from './faq/faq.js'
import { Invoices, InvoicesProps } from './invoices/invoices.js'
import { LanguageProps, Languages } from './languages/languages.js'
import { Login, LoginProps } from './login/login.js'
import { ViewOffers, ViewOffersProps } from './offers/offers.js'
import { SingleSubscription, SingleSubscriptionProps } from './single-subscription/single-subscription.js'
import { MySubscriptions, MySubscriptionsProps, ViewSubscriptionsBlockProps } from './subscriptions/subscriptions.js'
import { ClaimDamageAdditionalProps, SwapSummaryItem, ViewSwap, ViewSwapProps } from './swap/swap.js'
import { ViewUpgrade, ViewUpgradeProps } from './upgrade/upgrade.js'

export interface PortalProps {
    visualLocaleCode?: string
    dashboardProps?: DashboardProps
    loginProps?: LoginProps
    mySubscriptionProps?: MySubscriptionsProps
    singleSubscriptionProps?: SingleSubscriptionProps
    singleSubscriptionCancelProps?: ViewCancelProps
    singleSubscriptionSwapProps?: ViewSwapProps
    singleSubscriptionSwapSummaryProps?: ViewSwapProps
    singleSubscriptionSwapReceiptProps?: NotificationPageProps
    invoicesProps?: InvoicesProps
    languageProps?: LanguageProps
    accountProps?: ViewAccountProps
    faqProps?: ViewFaqProps
    offersProps?: ViewOffersProps
    upgradeProps?: ViewUpgradeProps
    debugProps?: DebugProps
}

type HeaderLink = 'dashboard' | 'subscriptions' | 'invoices' | 'account' | 'faq' | 'offers'

const getSingleSubscriptionsProps = (subscription: Subscription, view: View): SingleSubscriptionProps => {
    return {
        title: {
            alwaysButton: true,
            title: t('subscription.manageSubscription'),
            button: {
                url: `#/subscriptions/${subscription.uuid}`,
            },
        },
        card: subscription.getSubscriptionCardBaseProps(view),
        upgradeCards: subscription.getSubscriptionActionCards(),
        notificationCards: subscription.getSubscriptionNotificationCards(),
    }
}

const getDamageTypeSelectProps = (view: View, subscription: Subscription): SelectProps => {
    return {
        id: 'damage-type',
        name: 'damage-type',
        placeholder: t('subscription.cancel.damageType.placeholder'),
        options: [
            {
                value: ClaimDamageType.FELLANDBROKE,
                label: t('subscription.cancel.damageType.fellAndBroke'),
            },
            {
                value: ClaimDamageType.STOPPEDWORKING,
                label: t('subscription.cancel.damageType.stoppedWorking'),
            },
            {
                value: ClaimDamageType.LOSTORSTOLEN,
                label: t('subscription.cancel.damageType.lostOrStolen'),
            },
        ],
        value: view.state.portal.subscriptionClaim.damageType,
        error: view.state.formErrors.deviceDamaged ? t('general.errors.required') : undefined,
        onChange: (value) => {
            view.state.portal.subscriptionClaim.damageType = value as ClaimDamageType
            if (
                subscription.mainProduct.policyProvider === PolicyProvider.BOLTTECH &&
                value === ClaimDamageType.FELLANDBROKE
            ) {
                view.state.portal.subscriptionClaim.frontSideDamage = true
                view.state.portal.subscriptionClaim.backSideDamage = true
            }

            view.update()
        },
    }
}

const getSpecificDamageProps = (view: View, subscription: Subscription): CheckCardProps[] => {
    const { subscriptionClaim, subscriptionClaimFiles } = view.state.portal
    const maxNumberOfFiles = 2

    let frontSideDamageLabel = 'subscription.cancel.specificDamage.frontScreen'
    let frontSideDamageDescription = 'subscription.cancel.specificDamage.frontScreenDescription'
    let backSideDamageLabel = 'subscription.cancel.specificDamage.frontScreen'
    let backSideDamageDescription = 'subscription.cancel.specificDamage.frontScreenDescription'

    if (subscription.mainProduct.policyProvider === PolicyProvider.BOLTTECH) {
        frontSideDamageLabel = 'subscription.cancel.bolttech.specificDamage.frontScreen'
        frontSideDamageDescription = 'subscription.cancel.bolttech.specificDamage.frontScreenDescription'
        backSideDamageLabel = 'subscription.cancel.bolttech.specificDamage.frontScreen'
        backSideDamageDescription = 'subscription.cancel.bolttech.specificDamage.frontScreenDescription'
    }

    const uploadCards: CheckCardProps[] = [
        {
            icon: DamageFrontIcon,
            id: ClaimDamageLocation.FRONTSIDE,
            label: t(frontSideDamageLabel),
            checked: !!subscriptionClaim.frontSideDamage,
            description: t(frontSideDamageDescription),
            error: getTranslatedErrorMessage(
                'subscription.cancel.errors.frontSideDamageFiles',
                view.state.formErrors.frontSideDamageFiles,
            ),
            onChange: (newChecked) => {
                // for bolttech this is forced to true
                if (subscription.mainProduct.policyProvider !== PolicyProvider.BOLTTECH) {
                    subscriptionClaim.frontSideDamage = newChecked
                    view.update()
                }
            },
            children: subscriptionClaim.frontSideDamage && (
                <FileUpload
                    {...{
                        id: ClaimDamageLocation.FRONTSIDE + '-file',
                        name: ClaimDamageLocation.FRONTSIDE + '-file',
                        addText: t('subscription.cancel.specificDamage.frontScreenAddFile'),
                        label: t('subscription.cancel.specificDamage.frontScreenFile'),
                        removeText: t('subscription.cancel.specificDamage.frontScreenRemove'),
                        files: subscriptionClaimFiles.frontSideDamageFiles,
                        multiple: true,
                        input: {
                            accept: 'image/*',
                            size: 10,
                            max: maxNumberOfFiles,
                        },
                        onChange: (_event, files) => {
                            subscriptionClaimFiles.frontSideDamageFiles = [
                                ...subscriptionClaimFiles.frontSideDamageFiles,
                                ...files,
                            ].slice(0, maxNumberOfFiles)

                            view.update()
                        },
                        onRemove: (file) => {
                            subscriptionClaimFiles.frontSideDamageFiles =
                                subscriptionClaimFiles.frontSideDamageFiles.filter((f) => f !== file)
                            view.update()
                        },
                    }}
                />
            ),
            value: ClaimDamageLocation.FRONTSIDE,
        },
        {
            icon: DamageBackIcon,
            id: ClaimDamageLocation.BACKSIDE,
            label: t(backSideDamageLabel),
            checked: !!subscriptionClaim.backSideDamage,
            description: t(backSideDamageDescription),
            error: getTranslatedErrorMessage(
                'subscription.cancel.errors.backSideDamageFiles',
                view.state.formErrors.backSideDamageFiles,
            ),
            onChange: (newChecked) => {
                // for bolttech this is forced to true
                if (subscription.mainProduct.policyProvider !== PolicyProvider.BOLTTECH) {
                    subscriptionClaim.backSideDamage = newChecked
                    view.update()
                }
            },
            children: subscriptionClaim.backSideDamage && (
                <FileUpload
                    {...{
                        id: ClaimDamageLocation.BACKSIDE + '-file',
                        name: ClaimDamageLocation.BACKSIDE + '-file',
                        addText: t('subscription.cancel.specificDamage.backSideAddFile'),
                        label: t('subscription.cancel.specificDamage.backSideFile'),
                        removeText: t('subscription.cancel.specificDamage.backSideRemove'),
                        files: subscriptionClaimFiles.backSideDamageFiles,
                        multiple: true,
                        input: {
                            accept: 'image/*',
                            size: 10,
                            max: maxNumberOfFiles,
                        },
                        onChange: (_event, files) => {
                            subscriptionClaimFiles.backSideDamageFiles = [
                                ...subscriptionClaimFiles.backSideDamageFiles,
                                ...files,
                            ].slice(0, maxNumberOfFiles)

                            view.update()
                        },
                        onRemove: (file) => {
                            subscriptionClaimFiles.backSideDamageFiles =
                                subscriptionClaimFiles.backSideDamageFiles.filter((f) => f !== file)
                            view.update()
                        },
                    }}
                />
            ),
            value: ClaimDamageLocation.BACKSIDE,
        },
    ]
    const additionalCards: CheckCardProps[] = [
        {
            icon: DamageCameraIcon,
            id: ClaimDamageLocation.CAMERA,
            label: t('subscription.cancel.specificDamage.camera'),
            checked: !!subscriptionClaim.cameraProblems,
            description: t('subscription.cancel.specificDamage.cameraDescription'),
            onChange: (newChecked) => {
                subscriptionClaim.cameraProblems = newChecked
                view.update()
            },
            value: ClaimDamageLocation.CAMERA,
        },
        {
            icon: DamageAudioIcon,
            id: ClaimDamageLocation.AUDIO,
            label: t('subscription.cancel.specificDamage.audio'),
            checked: !!subscriptionClaim.audioProblems,
            description: t('subscription.cancel.specificDamage.audioDescription'),
            onChange: (newChecked) => {
                subscriptionClaim.audioProblems = newChecked
                view.update()
            },
            value: ClaimDamageLocation.AUDIO,
        },
        {
            icon: DamageNetworkIcon,
            id: ClaimDamageLocation.NETWORK,
            label: t('subscription.cancel.specificDamage.network'),
            checked: !!subscriptionClaim.networkProblems,
            description: t('subscription.cancel.specificDamage.networkDescription'),
            onChange: (newChecked) => {
                subscriptionClaim.networkProblems = newChecked
                view.update()
            },
            value: ClaimDamageLocation.NETWORK,
        },
        {
            icon: DamagePowerIcon,
            id: ClaimDamageLocation.POWER,
            label: t('subscription.cancel.specificDamage.power'),
            checked: !!subscriptionClaim.powerProblems,
            description: t('subscription.cancel.specificDamage.powerDescription'),
            onChange: (newChecked) => {
                subscriptionClaim.powerProblems = newChecked
                view.update()
            },
            value: ClaimDamageLocation.POWER,
        },
        {
            id: ClaimDamageLocation.OTHER,
            label: t('subscription.cancel.specificDamage.other'),
            checked: !!subscriptionClaim.other,
            description: t('subscription.cancel.specificDamage.otherDescription'),
            onChange: (newChecked) => {
                subscriptionClaim.other = newChecked
                view.update()
            },
            children: (
                <Textarea
                    {...{
                        id: ClaimDamageLocation.OTHER + '-description',
                        name: ClaimDamageLocation.OTHER + '-description',
                        bordered: true,
                        label: t('subscription.cancel.specificDamage.otherDescriptionLabel'),
                        placeholder: t('subscription.cancel.specificDamage.otherDescriptionPlaceholder'),
                        value: subscriptionClaim.otherDescription || '',
                        onChange: (value) => {
                            subscriptionClaim.otherDescription = value
                            view.update()
                        },
                    }}
                />
            ),
            value: ClaimDamageLocation.OTHER,
        },
    ]

    return [
        ...uploadCards,
        ...(subscription.mainProduct.policyProvider === PolicyProvider.BOLTTECH ? [] : additionalCards),
    ]
}

const getUpsellOptions = (subscription: Subscription) =>
    [
        {
            id: 'upsell-option-1',
            value: UpsellOptionType.SWAP,
            label: t('subscription.cancel.upsell.options.swap-device'),
        },
        {
            id: 'upsell-option-2',
            value: UpsellOptionType.CANCEL,
            label: t('subscription.cancel.upsell.options.cancel-sub'),
        },
        subscription.permissions?.canUpgrade
            ? {
                  id: 'upsell-option-3',
                  value: UpsellOptionType.UPGRADE,
                  label: t('subscription.cancel.upsell.options.upgrade-device'),
              }
            : undefined,
    ].filter(Boolean)

const getClaimDamageProps = (
    view: View,
    subscription: Subscription,
    type: 'cancel' | 'swap',
): ClaimDamageProps | undefined => {
    const hasDamage = view.state.portal.subscriptionClaim.deviceDamaged

    if ((type === 'cancel' && hasDamage) || type === 'swap') {
        return {
            title: t(`subscription.${type}.damageTitle`),
            damageSelect: getDamageTypeSelectProps(view, subscription),
            additional: getAdditionalDamageProps(view, subscription, type),
        }
    }

    return undefined
}

const getDamageDatepickerProps = (view: View, type: 'cancel' | 'swap'): DatepickerProps => {
    return {
        id: 'damage-date',
        name: 'damageDate',
        error: view.state.formErrors.damageDate ? t('general.errors.required') : undefined,
        label: t(`subscription.${type}.damageDate`),
        nextMonthText: t(`subscription.${type}.nextMonth`),
        prevMonthText: t(`subscription.${type}.prevMonth`),
        placeholder: t(`subscription.${type}.damageDatePlaceholder`),
        initialValue: view.state.portal.subscriptionClaim.dateOfDamage
            ? new Date(view.state.portal.subscriptionClaim.dateOfDamage)
            : new Date(),
        onChange: (date) => {
            view.state.portal.subscriptionClaim.dateOfDamage = date ? date.toISOString() : undefined
            view.update()
        },
    }
}

const getDamageDescriptionTextareaProps = (view: View, type: 'cancel' | 'swap'): TextareaProps => {
    return {
        id: 'damage-description',
        name: 'damageDescription',
        error: view.state.formErrors.damageDescription ? t('general.errors.required') : undefined,
        label: t(`subscription.${type}.damageDescription`),
        placeholder: t(`subscription.${type}.damageDescriptionPlaceholder`),
        value: view.state.portal.subscriptionClaim.damageDescription || '',
        onChange: (value) => {
            view.state.portal.subscriptionClaim.damageDescription = value
            view.update()
        },
    }
}

const getAdditionalDamageProps = (
    view: View,
    subscription: Subscription,
    type: 'cancel' | 'swap',
): ClaimDamageAdditionalProps | undefined => {
    switch (subscription.mainProduct.policyProvider) {
        case PolicyProvider.BOLTTECH:
            if (type === 'cancel') {
                switch (view.state.portal.subscriptionClaim.damageType) {
                    case ClaimDamageType.FELLANDBROKE:
                        return {
                            title: t(`subscription.cancel.damageAdditionalTitle`),
                            specificDamage: getSpecificDamageProps(view, subscription),
                            damageTitle: t(`subscription.cancel.bolttech.damageSpecTitle`),
                        }
                }
            }

            return {}

        default:
            switch (view.state.portal.subscriptionClaim.damageType) {
                case ClaimDamageType.LOSTORSTOLEN:
                    return {
                        datepicker: getDamageDatepickerProps(view, type),
                        textarea: getDamageDescriptionTextareaProps(view, type),
                    }

                case ClaimDamageType.FELLANDBROKE:
                    return {
                        title: t(`subscription.${type}.damageAdditionalTitle`),
                        specificDamage: getSpecificDamageProps(view, subscription),
                        datepicker: getDamageDatepickerProps(view, type),
                        textarea: getDamageDescriptionTextareaProps(view, type),
                        damageTitle: t(`subscription.${type}.damageSpecTitle`),
                    }
            }
            break
    }
}

const getDeviceConditionProps = (subscription: Subscription, view: View): ChooseMoreProps | undefined => {
    const isValue = subscription.mainProduct.plan.code === PlanCode.VALUE
    const hasDamage = view.state.portal.subscriptionClaim.deviceDamaged
    const returnSelected = view.state.portal.subscriptionClaim.productResolution === ClaimProductResolution.RETURN

    if (isValue && !returnSelected) {
        return undefined
    }

    return {
        id: 'conditions-choice',
        type: 'radio',
        label: t('subscription.cancel.conditionChoice'),
        name: 'conditions-choice',
        button: {
            text: t('subscription.cancel.conditionsButton'),
        },
        openModalValue: 'has-damage',
        value: hasDamage ? 'has-damage' : 'no-damage',
        onChange: (value) => {
            view.state.portal.subscriptionClaim.deviceDamaged = value === 'has-damage'
            view.update()
        },
        options: [
            {
                id: 'no-damage',
                value: 'no-damage',
                label: (
                    <div className="text">
                        <p>
                            <b>{t('subscription.cancel.noDamage')}</b>
                        </p>
                        <p>{t('subscription.cancel.noDamageDescription')}</p>
                    </div>
                ),
            },
            {
                id: 'has-damage',
                value: 'has-damage',
                label: (
                    <div className="text">
                        <p>
                            <b>{t('subscription.cancel.hasDamage')}</b>
                        </p>
                        <p>{t('subscription.cancel.hasDamageDescription')}</p>
                    </div>
                ),
            },
        ],
    }
}

const getUpsellModalProps = (subscription: Subscription, view: View): UpsellProps => {
    return {
        id: 'upsell-modal',
        type: 'radio',
        name: 'upsell',
        title: t('subscription.cancel.upsell.title'),
        label: t('subscription.cancel.upsell.content'),
        options: getUpsellOptions(subscription),
        value: view.state.portal.upsellChoice,
        onChange: (value) => {
            view.state.portal.upsellChoice = value as UpsellOptionType
            view.update()
        },
        confirmButton: {
            text: t('subscription.cancel.upsell.confirmButton'),
            onClick: () => {
                switch (view.state.portal.upsellChoice) {
                    case UpsellOptionType.SWAP:
                        window.location.href = `#/subscriptions/${subscription.uuid}/swap`
                        break
                    case UpsellOptionType.UPGRADE:
                        window.location.href = `#/subscriptions/${subscription.uuid}/manage`
                        break
                    default:
                    case UpsellOptionType.CANCEL:
                        // we continue cancel flow
                        break
                }
            },
        },
        closeButton: {
            text: t('subscription.cancel.upsell.closeButton'),
        },
    }
}

const getTermsCheckboxProps = (subscription: Subscription, view: View): CheckboxProps | undefined => {
    const isValue = subscription.mainProduct.plan.code === PlanCode.VALUE
    const hasDamage = view.state.portal.subscriptionClaim.deviceDamaged
    const returnSelected = view.state.portal.subscriptionClaim.productResolution === ClaimProductResolution.RETURN
    const deviceFellAndBroke = view.state.portal.subscriptionClaim.damageType !== ClaimDamageType.FELLANDBROKE

    if (isValue && !returnSelected) {
        return undefined
    }

    if (hasDamage && deviceFellAndBroke) {
        return undefined
    }

    return {
        id: 'terms-checkbox',
        value: 'terms',
        label: t('subscription.cancel.termsLabel'),
        checked: view.state.portal.subscriptionClaim.termsAndConditions,
        onChange: (value) => {
            view.state.portal.subscriptionClaim.termsAndConditions = value
            view.update()
        },
    }
}

const getDeviceConditionTermsProps = (
    view: View,
    subscription: Subscription,
    type: 'swap' | 'cancel',
): NotificationCardProps | undefined => {
    const isCancel = type === 'cancel'

    if (isCancel && !view.state.portal.subscriptionClaim.deviceDamaged) {
        return undefined
    }

    if (subscription.mainProduct.policyProvider === PolicyProvider.BOLTTECH) {
        return {
            content: (
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore ReactI18N type definition issue
                <Trans i18nKey="subscription.swap.bolttech.deviceDamagedConditionTerms">
                    <p>
                        Om enheten skadas eller försvinner behöver du ersätta Lisa& för skadan som orsakats.I Samsung
                        Flex ingår försäkringen Samsung Care+ som täcker oavsiktliga ta pp- och vattenskador och då ska
                        ett försäkringsärende skapas. För vissa andra skador kan Samsungs garanti tillämpas.
                    </p>
                    <p className="bold">
                        Fyll i formuläret för att få hjälp att identifiera hur ditt problem bäst löses.
                    </p>
                </Trans>
            ),
        }
    }

    return {
        content: (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore ReactI18N type definition issue
            <Trans i18nKey="subscription.deviceDamagedConditionTerms">
                <p>
                    Om Enheten skadas eller försvinner måste du ersätta Foxway för den skada som orsakats. För
                    oavsiktliga tapp- och vattenskador betalar du en begränsad skadeavgift för att få en
                    ersättningsenhet. Avgiften varierar beroende på vilken modell du har. Fyll i frågorna nedan för att
                    anmäla en skada och få Foxways bedömning om din skada kvalificerar dig för en Swap service.
                </p>
                <p>
                    Om din enhet blir stulen eller tappas bort erbjuder Foxway dig att betala en avgift för att ersätta
                    telefonen. Avgiften motsvarar marknadsvärde vid tidpunkten. Kontakta kundservice för att anmäla en
                    borttappad telefon.
                </p>
                <a href={t('subscription.deviceDamagedConditionTermsLink')}>
                    Se fullständiga villkor för Swap service här
                </a>
            </Trans>
        ),
    }
}

const getSwapSubmitButtonProps = (subscription: Subscription, view: View): ButtonProps => {
    let isValid = false
    let text = t(`subscription.swap.submitButton`)

    switch (subscription.mainProduct.policyProvider) {
        case PolicyProvider.BOLTTECH:
            text = t(`subscription.swap.bolttech.submitButton`)
            switch (view.state.portal.subscriptionClaim.damageType) {
                case ClaimDamageType.FELLANDBROKE:
                case ClaimDamageType.LOSTORSTOLEN:
                    isValid = view.state.portal.subscriptionClaim.termsAndConditions
            }
            break

        default:
            switch (view.state.portal.subscriptionClaim.damageType) {
                case ClaimDamageType.LOSTORSTOLEN:
                    text = t(`subscription.swap.registerButton`)
                    isValid = view.state.portal.subscriptionClaim.termsAndConditions
                    break
                case ClaimDamageType.FELLANDBROKE:
                    isValid = view.state.portal.subscriptionClaim.termsAndConditions
                    break
            }
            break
    }

    return {
        isLoading: view.state.isLoading,
        isDisabled: !isValid,
        text,
        onClick: () => {
            void view.api.portal.swapSubscription(subscription.uuid)
        },
    }
}
const getCancelSubmitButtonProps = (subscription: Subscription, view: View): ButtonProps => {
    const hasDamage = view.state.portal.subscriptionClaim.deviceDamaged
    const termsAccepted = view.state.portal.subscriptionClaim.termsAndConditions
    const isFlex = subscription.mainProduct.plan.code === PlanCode.FLEX
    const returnSelected = view.state.portal.subscriptionClaim.productResolution === ClaimProductResolution.RETURN
    const deviceFellAndBroke = view.state.portal.subscriptionClaim.damageType === ClaimDamageType.FELLANDBROKE

    const isValid =
        termsAccepted &&
        ((returnSelected && !hasDamage) ||
            (returnSelected && hasDamage && deviceFellAndBroke) ||
            (isFlex && !hasDamage) ||
            (isFlex && hasDamage && deviceFellAndBroke))

    const buttonText =
        subscription.mainProduct.policyProvider === PolicyProvider.BOLTTECH
            ? `subscription.cancel.bolttech.submitButton`
            : `subscription.cancel.submitButton`

    return {
        isLoading: view.state.isLoading,
        isDisabled: !isValid,
        text: t(buttonText),
        onClick: () => {
            void view.api.portal.cancelSubscription(subscription.uuid)
        },
    }
}

const getSingleSubscriptionCancelProps = (subscription: Subscription, view: View): ViewCancelProps => {
    return {
        title: {
            title: t('subscription.cancel.title'),
            button: {
                url: `#/subscriptions/${subscription.uuid}/manage`,
            },
        },
        formTitle: t('subscription.cancel.formTitle'), // Few details before you can go
        subscription: subscription.getSubscriptionCardBaseProps(view),
        actionChoice: subscription.getCancellationActionChoiceSelectProps(view),
        disclaimer: subscription.renderCancellationDisclaimer(),
        conditionChoice: getDeviceConditionProps(subscription, view),
        conditionTerms: getDeviceConditionTermsProps(view, subscription, 'cancel'),
        termsCheckbox: getTermsCheckboxProps(subscription, view),
        upsell:
            subscription.mainProduct.policyProvider === PolicyProvider.BOLTTECH
                ? undefined
                : getUpsellModalProps(subscription, view),
        submitButton: getCancelSubmitButtonProps(subscription, view),
        damageProps: getClaimDamageProps(view, subscription, 'cancel'),
        notifications: getClaimNotificationsProps(view, subscription, 'cancel'),
    }
}

const getSingleSubscriptionSwapProps = (subscription: Subscription, view: View): ViewSwapProps => {
    const damageType = view.state.portal.subscriptionClaim.damageType
    let canContinue = damageType === ClaimDamageType.FELLANDBROKE || damageType === ClaimDamageType.LOSTORSTOLEN
    const policyProvider = subscription.mainProduct.policyProvider
    let url = `#/subscriptions/${subscription.uuid}/swap-summary`
    const countryCode = subscription.country?.code.toLowerCase()

    let title = `subscription.swap.title`
    let buttonText = `subscription.swap.continueButton`
    if (policyProvider === PolicyProvider.BOLTTECH) {
        title = `subscription.swap.bolttech.title`
        buttonText = `subscription.swap.bolttech.continueButton`
        canContinue = damageType === ClaimDamageType.FELLANDBROKE
        url =
            process.env.APP_BOLTTECH_PORTAL_URL!.replace('{{countryCode}}.', countryCode ? countryCode + '.' : '') +
            '?repairType=active'
    }

    return {
        title: {
            title: t(title),
            button: {
                onClick: view.back,
            },
        },
        formTitle: t('subscription.swap.formTitle'),
        conditionTerms: getDeviceConditionTermsProps(view, subscription, 'swap'),
        button: {
            text: t(buttonText),
            url,
            isLoading: view.state.isLoading,
            isDisabled: !canContinue,
        },
        subscription: subscription.getSubscriptionCardBaseProps(view),
        damageProps: getClaimDamageProps(view, subscription, 'swap'),
        notifications: getClaimNotificationsProps(view, subscription, 'swap'),
    }
}

const getSwapSummaryItems = (subscription: Subscription, view: View): SwapSummaryItem[] => {
    const items: SwapSummaryItem[] = []

    items.push({
        title: t('subscription.swap.summary.productTitle'),
        values: [
            `${subscription.mainProduct.data.product?.translation?.name}, ${subscription.mainProduct.data.product?.description}`,
        ],
    })

    items.push({
        title: t('subscription.swap.summary.customerTitle'),
        values: [`${subscription.mainProduct.customer?.fullName}`],
    })

    if (subscription.contractStartDate) {
        items.push({
            title: t('subscription.swap.summary.periodTitle'),
            values: [`${format(subscription.contractStartDate, 'dd.MM.yyyy')}`],
        })
    }

    items.push({
        title: t('subscription.swap.summary.deviceDetailsTitle'),
        values: [`${subscription.mainProduct.sku}`, `${subscription.mainProduct.data.productImei}`],
    })

    const overviewList = [
        ...(view.state.portal.subscriptionClaim.frontSideDamage &&
        view.state.portal.subscriptionClaimFiles.frontSideDamageFiles
            ? [
                  `<strong>${t('subscription.swap.summary.front-side')}</strong> ${t('general.images', {
                      count: view.state.portal.subscriptionClaimFiles.frontSideDamageFiles.length,
                  })}`,
              ]
            : []),
        ...(view.state.portal.subscriptionClaim.backSideDamage &&
        view.state.portal.subscriptionClaimFiles.backSideDamageFiles
            ? [
                  `<strong>${t('subscription.swap.summary.back-side')}</strong> ${t('general.images', {
                      count: view.state.portal.subscriptionClaimFiles.backSideDamageFiles.length,
                  })}`,
              ]
            : []),
        ...(view.state.portal.subscriptionClaim.cameraProblems
            ? [`<strong>${t('subscription.swap.summary.camera')}</strong>`]
            : []),
        ...(view.state.portal.subscriptionClaim.audioProblems
            ? [`<strong>${t('subscription.swap.summary.audio')}</strong>`]
            : []),
        ...(view.state.portal.subscriptionClaim.otherDescription
            ? [
                  `<strong>${t('subscription.swap.summary.other')}</strong><br>${
                      view.state.portal.subscriptionClaim.otherDescription
                  }`,
              ]
            : []),
    ]

    items.push({
        title: t('subscription.swap.summary.overviewTitle'),
        subValues: [
            {
                title: t('subscription.swap.summary.overviewIssueTitle'),
                value: t(`subscription.swap.summary.overview.issues.${view.state.portal.subscriptionClaim.damageType}`),
            },
            ...(view.state.portal.subscriptionClaim.damageDescription
                ? [
                      {
                          title: t('subscription.swap.summary.overviewDamageDescriptionTitle'),
                          value: `${view.state.portal.subscriptionClaim.damageDescription}`,
                      },
                  ]
                : []),
            ...(view.state.portal.subscriptionClaim.dateOfDamage
                ? [
                      {
                          title: t('subscription.swap.summary.overviewDateTitle'),
                          value: `${format(new Date(view.state.portal.subscriptionClaim.dateOfDamage), 'dd.MM.yyyy')}`,
                      },
                  ]
                : []),
            ...(overviewList.length
                ? [
                      {
                          title: t('subscription.swap.summary.overviewDamagesTitle'),
                          list: overviewList,
                      },
                  ]
                : []),
        ].filter((e) => e),
    })

    return items
}

const getSingleSubscriptionSwapSummeryProps = (subscription: Subscription, view: View): ViewSwapProps => {
    return {
        showSummary: true,
        title: {
            title: t('subscription.swap.title'),
            button: {
                onClick: view.back,
            },
        },
        formTitle: t('subscription.swap.summaryTitle'),
        summary: {
            items: getSwapSummaryItems(subscription, view),
        },
        button: getSwapSubmitButtonProps(subscription, view),
        termsCheckbox: {
            id: 'terms-checkbox',
            value: 'terms',
            label: t('subscription.swap.termsLabel'),
            checked: view.state.portal.subscriptionClaim.termsAndConditions,
            onChange: (value) => {
                view.state.portal.subscriptionClaim.termsAndConditions = value
                view.update()
            },
        },
    }
}

const getSingleSubscriptionClaimProps = (
    subscription: Subscription,
    claimId: string,
    view: View,
): Partial<PortalProps> => {
    const claim = subscription.findClaim(claimId)

    if (claim) {
        switch (claim.claimStatus) {
            case ClaimStatus.PAYMENTERROR:
                return {
                    singleSubscriptionSwapReceiptProps: {
                        title: t('subscription.swap.paymentError.title', {
                            id: claim.visualID,
                        }),
                        children: t('subscription.swap.paymentError.content'),
                        button: {
                            text: t('subscription.swap.paymentError.button'),
                            url: subscription.getClaimPayUrl(claim.id),
                        },
                    },
                }

            case ClaimStatus.PAYMENTRECEIVED:
                return {
                    singleSubscriptionSwapReceiptProps: {
                        title: t('subscription.swap.paymentSuccess.title', {
                            id: claim.visualID,
                        }),
                        children: (
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore ReactI18N type definition issue
                            <Trans i18nKey="subscription.swap.paymentSuccess.content">
                                You can view all of your ongoing Swap Service Applications and their statuses under{' '}
                                <a href="#/subscriptions">My Subscriptions</a>
                                <p>Your application will be handled within 1 business day.</p>
                            </Trans>
                        ),
                        button: {
                            text: t('subscription.swap.paymentSuccess.button'),
                            onClick: () => {
                                window.location.href = '#/'
                            },
                        },
                    },
                }
        }
    }

    return {
        singleSubscriptionSwapProps: getSingleSubscriptionSwapProps(subscription, view),
    }
}

const getSingleSubscriptionSwapReceiptProps = (
    subscription: Subscription,
    view: View,
): NotificationPageProps | undefined => {
    if (!view.state.portal.latestSubscriptionClaim) {
        window.location.hash = `${window.location.hash.replace('/swap-receipt', '')}`
        view.update()
        return
    }

    return {
        title: t('subscription.swap.receipt.title', {
            id: view.state.portal.latestSubscriptionClaim.visualID,
        }),
        children: (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore ReactI18N type definition issue
            <Trans i18nKey="subscription.swap.receipt.content">
                You can view all of your ongoing Swap Service Applications and their statuses under{' '}
                <a href="#/subscriptions">My Subscriptions</a>
                <p>Your application will be handled within 1 business day.</p>
            </Trans>
        ),
        button: {
            text: t('subscription.swap.receipt.button'),
            onClick: () => {
                window.location.href = '#/'
            },
        },
    }
}

const getClaimNotificationsProps = (view: View, subscription: Subscription, type: 'cancel' | 'swap') => {
    const claim = view.state.portal.subscriptionClaim
    const notifications = []

    if (claim.deviceDamaged || type === 'swap') {
        if (subscription.mainProduct.policyProvider === PolicyProvider.BOLTTECH) {
            switch (claim.damageType) {
                case ClaimDamageType.FELLANDBROKE:
                    notifications.push({
                        title: t(`subscription.${type}.bolttech.fellAndBrokeNotificationTitle`),
                        content: t(`subscription.${type}.bolttech.fellAndBrokeNotificationContent`),
                    })
                    break

                case ClaimDamageType.LOSTORSTOLEN:
                    notifications.push({
                        title: t(`subscription.${type}.bolttech.lostOrStolenNotificationTitle`),
                        content: t(`subscription.${type}.bolttech.lostOrStolenNotificationContent`),
                    })
                    break

                case ClaimDamageType.STOPPEDWORKING:
                    notifications.push({
                        title: t(`subscription.${type}.bolttech.stoppedWorkingNotificationTitle`),
                        content: t(`subscription.${type}.bolttech.stoppedWorkingNotificationContent`),
                    })
                    break
            }
        } else {
            switch (claim.damageType) {
                case ClaimDamageType.LOSTORSTOLEN:
                    notifications.push(getNotSupportedNotificationProps(type))
                    break

                case ClaimDamageType.STOPPEDWORKING:
                    notifications.push(getWarrantyNotificationProps(type))
                    break
            }
        }
    }

    const hasUnpaidInvoices =
        view.state.portal.paymentStatus?.type === 'paymentDue' &&
        view.state.portal.subscriptionClaim.productResolution !== ClaimProductResolution.UPGRADE
    if (
        type === 'cancel' &&
        hasUnpaidInvoices &&
        view.state.portal.subscriptionClaim.productResolution === ClaimProductResolution.RETURN
    ) {
        notifications.push(getInvoicesDisclaimer(type))
    }

    if (type === 'cancel' && view.state.portal.subscriptionClaim.productResolution === ClaimProductResolution.UPGRADE) {
        notifications.push(getUpgradeDisclaimer(view))
    }

    if (type === 'cancel' && view.state.portal.subscriptionClaim.productResolution === ClaimProductResolution.KEEP) {
        notifications.push(getKeepDisclaimer())
    }

    return notifications.length ? notifications : undefined
}

const getKeepDisclaimer = (): { title: string; content: string } => {
    return {
        title: t('subscription.cancel.keepDisclaimerTitle'),
        content: t('subscription.cancel.keepDisclaimerContent'),
    }
}

const getUpgradeDisclaimer = (
    _view: View,
): { title: string; content: string; link: { text: string; href: string } } => {
    return {
        title: t('subscription.cancel.upgradeDisclaimerTitle'),
        content: t('subscription.cancel.upgradeDisclaimerContent'),
        link: {
            text: t('subscription.cancel.upgradeDisclaimerLink'),
            href: `${window.location.hash.replace('/cancel', '/manage')}`,
        },
    }
}

const getInvoicesDisclaimer = (type: 'cancel' | 'swap'): { modifier: string; title: string; content: string } => {
    return {
        modifier: 'card--bg-red',
        title: t(`subscription.${type}.invoicesDisclaimerTitle`),
        content: t(`subscription.${type}.invoicesDisclaimerContent`),
    }
}

const getNotSupportedNotificationProps = (type: 'cancel' | 'swap'): { title: string; content: string } => {
    return {
        title: t(`subscription.${type}.notSupportedNotificationTitle`),
        content: t(`subscription.${type}.notSupportedNotificationContent`),
    }
}

const getWarrantyNotificationProps = (type: 'cancel' | 'swap'): { title: string; content: string } => {
    return {
        title: t(`subscription.${type}.warrantyNotificationTitle`),
        content: t(`subscription.${type}.warrantyNotificationContent`),
    }
}

const getMySubscriptionsProps = (view: View, subscriptionUuid?: string, showPrevious = false): MySubscriptionsProps => {
    const { subscriptions } = view.state.portal
    const previousItems: SubscriptionCardProps[] = []
    const flexPlans: ViewSubscriptionsBlockProps = {
        title: t('subscription.flexTitle'),
        items: [],
    }
    const valuePlans: ViewSubscriptionsBlockProps = {
        title: t('subscription.valueTitle'),
        items: [],
    }
    const pendingPayments: ViewSubscriptionsBlockProps = {
        title: t('subscription.pendingPaymentsTitle'),
        items: [],
    }

    if (subscriptions) {
        subscriptions
            .filter((subscription) => (showPrevious ? subscription.hasEnded : !subscription.hasEnded))
            .forEach((subscription) => {
                const itemProps = subscription.getSubscriptionCardProps(view, subscriptionUuid === subscription.uuid)

                if (subscription.status === SubscriptionStatus.PENDING) {
                    pendingPayments.items.push(itemProps)
                    return
                }

                if (showPrevious) {
                    previousItems.push(itemProps)
                } else {
                    if (subscription.currentPlan?.isFlex) {
                        flexPlans.items.push(itemProps)
                    } else {
                        valuePlans.items.push(itemProps)
                    }
                }
            })
    }

    if (showPrevious) {
        return {
            title: {
                alwaysButton: true,
                title: t('subscription.devicesTitle'),
                button: {
                    url: '#/subscriptions',
                },
            },
            items: previousItems,
        }
    }

    const subscriptionBlocks = []
    if (pendingPayments.items.length > 0) {
        subscriptionBlocks.push(pendingPayments)
    }

    if (flexPlans.items.length > 0) {
        subscriptionBlocks.push(flexPlans)
    }

    if (valuePlans.items.length > 0) {
        subscriptionBlocks.push(valuePlans)
    }

    return {
        title: {
            title: t('subscription.subscriptionsTitle'),
        },
        items: subscriptionBlocks,
        addNewButton: {
            url: t('subscription.addNewSubscriptionLink'),
            text: t('subscription.addNewSubscription'),
            target: '_blank',
        },
        previousButton: {
            url: '#/previous-devices',
            text: t('subscription.devicesTitle'),
        },
    }
}

const getInvoicesProps = (view: View): InvoicesProps => {
    const { invoices } = view.state.portal
    if (invoices === undefined) {
        void view.api.portal.loadCustomerInvoices()
    }

    return {
        title: t('portal.invoices.title'),
        invoices: invoices || [],
        tableHeader: t('portal.invoices.tableHeader', { returnObjects: true }) as string[],
        viewInvoiceText: t('portal.invoices.viewInvoice'),
        accordion: {
            title: t('portal.invoices.accordion.title'),
            text: t('portal.invoices.accordion.text'),
        },
        noInvoices: t('portal.invoices.noInvoices'),
        loadMore: t('portal.invoices.loadMore'),
        isLoading: view.state.isLoading,
    }
}

const getDashboardProps = (view: View, customer: Customer): DashboardProps => {
    const getPaymentCard = (): PaymentCardProps => {
        const { paymentStatus } = view.state.portal
        const button = {
            url: '#/invoices',
            text: t('portal.invoiceStatus.button'),
        }

        if (paymentStatus) {
            switch (paymentStatus.type) {
                case 'paymentDue':
                    return {
                        button,
                        content: (
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore ReactI18N type definition issue
                            <Trans
                                i18nKey="portal.invoiceStatus.dueOn"
                                values={{
                                    date: format(new Date(paymentStatus.dueDate || new Date()), 'dd.MM.yyyy'),
                                }}
                            >
                                Next payment due on{' '}
                                <strong>{format(new Date(paymentStatus.dueDate || new Date()), 'dd.MM.yyyy')}</strong>.
                            </Trans>
                        ),
                        price: formatPrice(paymentStatus.amount, paymentStatus.currency),
                        title: t('portal.invoiceStatus.due'),
                        type: 'error',
                    }

                default:
                case 'allPaid':
                    return {
                        button,
                        price: formatMonthlyPrice(paymentStatus.nextAmount, paymentStatus.currency, ''), // we use empty string to force current language
                        content: paymentStatus.nextDueDate ? (
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore ReactI18N type definition issue
                            <Trans
                                i18nKey="portal.invoiceStatus.nextPayment"
                                values={{
                                    date: format(new Date(paymentStatus.nextDueDate), 'dd.MM.yyyy'),
                                }}
                            >
                                Next payment due on{' '}
                                <strong>{format(new Date(paymentStatus.nextDueDate), 'dd.MM.yyyy')}</strong>.
                            </Trans>
                        ) : (
                            <>{t('portal.invoiceStatus.noNextPayment')}</>
                        ),
                        title: t('portal.invoiceStatus.allPaid'),
                        type: 'success',
                    }
            }
        }

        return {
            button,
            isLoading: true,
            price: '...',
            content: <>...</>,
            title: '...',
        }
    }

    const activeSubscriptions = view.state.portal.subscriptions?.filter((subscription) => !subscription.hasEnded) || []

    return {
        title: t('portal.dashboard.title'),
        subtitle: customer.fullName,
        paymentCard: getPaymentCard(),
        linkCards: [
            {
                icon: SubscriptionsIcon,
                text: t('header.subscriptions'),
                count: activeSubscriptions.length,
                url: '#/subscriptions',
            },
            {
                icon: InvoicesIcon,
                text: t('header.invoices'),
                url: '#/invoices',
            },
            {
                icon: AccountIcon,
                text: t('header.account'),
                url: '#/account/contact',
            },
        ],
        esisTag: customer.hasEsisOffers
            ? {
                  context: 'samsung-flex-portal',
                  content: 'banner',
                  customerUuid: customer.uuid,
                  language: formatLocaleForEsisTag(customer.localeCode),
              }
            : undefined,
    }
}

const getCustomerHeader = (view: View, route: Route, headerLink: HeaderLink): HeaderFlexProps => {
    const { state, update, api } = view
    const { header } = state.portal

    const mobileAccountItem: HeaderFlexItemProps = {
        hideInDesktop: true,
        label: t('header.account'),
        icon: header.isAccountExpanded ? ChevronDownIcon : ChevronRightIcon,
        onClick: () => {
            header.isAccountExpanded = !header.isAccountExpanded
            update()
        },
    }

    if (header.isAccountExpanded) {
        mobileAccountItem.children = [
            {
                icon: ContactIcon,
                label: t('profile.contactInformation.title'),
                url: '#/account/contact',
            },
            {
                icon: WalletIcon,
                label: t('profile.billingInformation.title'),
                url: '#/account/billing',
            },
        ]
    }

    const items: HeaderFlexItemProps[] = [
        {
            label: t('header.logOut'),
            type: 'auth',
            onClick: async () => {
                try {
                    await api.portal.logout()
                    window.location.href = '/#/'
                } catch (err) {
                    window.location.reload()
                }
            },
        },
        {
            label: t('header.home'),
            url: '#/',
            isCurrent: headerLink === 'dashboard',
            icon: ChevronRightIcon,
        },
        {
            label: t('header.subscriptions'),
            url: '#/subscriptions',
            isCurrent: headerLink === 'subscriptions',
            icon: ChevronRightIcon,
        },
        {
            label: t('header.invoices'),
            url: '#/invoices',
            isCurrent: headerLink === 'invoices',
            icon: ChevronRightIcon,
        },
        {
            hideInMobile: true,
            label: t('header.account'),
            url: '#/account/contact',
            isCurrent: headerLink === 'account',
            icon: ChevronRightIcon,
        },
        {
            label: t('header.offers'),
            url: '#/offers',
            isCurrent: headerLink === 'offers',
            icon: ChevronRightIcon,
        },
        mobileAccountItem,
    ]

    if (state.debugOptions.language) {
        items.push({
            label: 'Disable debug',
            type: 'debug',
            onClick: () => {
                state.debugOptions.language = undefined
                window.location.reload()
            },
        })
    }

    const props: HeaderFlexProps = {
        url: '#/',
        items,
        toggleLabel: t('header.toggleLabel'),
        isOpen: header.isOpen,
        onChange: (isOpen) => {
            header.isOpen = isOpen
            update()
        },
    }

    if (isPaymentMethodExpiring(state)) {
        props.alert = {
            items: getAlertItems(view, route),
        }
    }

    return props
}

const getAlertItems = (view: View, route: Route): AlertItemProps[] => {
    const { state, api } = view
    const isBillingView = route.view === PortalViewPaths.ACCOUNT && route.accountView === 'billing'

    if (isBillingView) {
        const dueDate = getExpiringPaymentMethodDueDate(state).formatWithDayJs('DD/MM/YYYY')

        return [
            {
                text: t('portal.billingAlert.detailed.before', { dueDate }),
            },
            {
                text: t('portal.billingAlert.detailed.link'),
                onClick: () => void api.portal.updatePaymentMethod(),
            },
            {
                text: t('portal.billingAlert.detailed.after'),
            },
        ]
    }

    return [
        {
            text: t('portal.billingAlert.generic.before'),
        },
        {
            text: t('portal.billingAlert.generic.link'),
            url: '#/account/billing',
        },
        {
            text: t('portal.billingAlert.generic.after'),
        },
    ]
}

const getEmptyHeader = (): HeaderFlexProps => {
    return {
        url: '#/',
        items: [],
        toggleLabel: t('header.toggleLabel'),
    }
}

const getCurrentCountryId = (view: View): number | null => {
    if (view.state.portal.languages === undefined) {
        void view.api.portal.loadLocales()
        return null
    }

    // Get possible locale from URL
    const url = window.location.href
    const hashIndex = url.indexOf('#')
    const hash = hashIndex !== -1 ? url.slice(hashIndex + 1) : ''
    const parts = hash.split('/')
    const currentLocale = parts[1]

    const matchingCountryCode = view.state.portal.languages.locales.find((item) => {
        return item.visualCode === currentLocale
    })

    if (!matchingCountryCode) {
        return null
    }

    return matchingCountryCode.id
}

export const getPortalFooterProps = (): FooterProps => {
    const columns = t('portalFooter.items', { returnObjects: true })

    return {
        copyright: t('portalFooter.copyright', { currentYear: new Date().getFullYear() }),
        columns: columns as Array<FooterColumnProps | ButtonProps>,
    }
}

const getLoginProps = (view: View): RootProps => {
    if (view.state.portal.loginAuthMethods === undefined) {
        const countryId = getCurrentCountryId(view)
        if (countryId) {
            void view.api.portal.loadAuthenticationMethods(countryId + '')
        }
    }

    return {
        portalHeader: getEmptyHeader(),
        portalFooter: getPortalFooterProps(),
        portal: {
            loginProps: {
                title: t('header.login'),
                subtitle: t('header.loginSubtitle'),
                authenticationMethodField: {
                    id: 'authenticationMethod',
                    name: 'loginAuthenticationMethod',
                    value: view.state.formData.authenticationMethod,
                    options:
                        view.state.portal.loginAuthMethods?.map((item) => {
                            return {
                                id: `${item.authMethod.providerName}_${item.authMethod.bankName}`,
                                value: `${item.authMethod.providerName}_${item.authMethod.bankName}`,
                                text: `${item.authMethod.visualName}`,
                            }
                        }) || [],
                    onChange: (value) => {
                        if (value) {
                            view.state.formData.authenticationMethod = value
                            view.update()
                        }
                    },
                },
                submitButton: {
                    isBlock: true,
                    variant: 'tertiary',
                    isDisabled: view.state.formData.authenticationMethod === '',
                    text: t('header.loginButton'),
                    onClick: () => {
                        view.api.portal.login().catch(console.error)
                    },
                },
            },
        },
    }
}

const getLanguageSelectProps = (view: View): RootProps => {
    if (view.state.portal.languages === undefined) {
        void view.api.portal.loadLocales()
    }

    return {
        portalHeader: getEmptyHeader(),
        portalFooter: getPortalFooterProps(),
        portal: {
            languageProps: {
                title: t('header.language.label'),
                subtitle: t('header.language.labelDescription'),
                languageSelector: {
                    id: 'languageSelector',
                    name: t('header.language.selectLabel'),
                    options:
                        view.state.portal.languages?.locales.map((item) => {
                            return {
                                id: `${item.id}`,
                                value: `${item.code.substring(0, 2)}`,
                                text: t(`header.language.options.${item.code}`),
                            }
                        }) || [],
                    onChange: (value) => {
                        if (value && view.state.portal) {
                            view.state.portal.loginAuthMethods = undefined
                            window.location.href = `${process.env.APP_CLIENT_BASE_URL}/#/${value}/login`
                            view.update()
                        }
                    },
                },
            },
        },
    }
}

const getDebugProps = (view: View): RootProps => {
    if (view.state.portal.debug === undefined) {
        void view.api.portal.loadDebug()
    }

    const subscriptionSelect = view.state.portal.debug?.subscriptions
        ? ({
              id: 'subscriptionSelect',
              name: 'subscriptionUuid',
              live: true,
              value: '',
              options: view.state.portal.debug.subscriptions.map((subscription) => {
                  return {
                      value: subscription.uuid,
                      label: `[${subscription.basketId}] - ${subscription.uuid} `,
                  }
              }),
              onChange: (value: string) => {
                  window.location.href = `${view.state.apiBaseUrl}/v1/debug?action=auth&subscriptionUuid=${value}`
              },
          } as SelectProps)
        : undefined

    return {
        portalHeader: getEmptyHeader(),
        portalFooter: getPortalFooterProps(),
        portal: {
            debugProps: {
                title: t('header.debug'),
                subscriptionSelect,
            },
        },
    }
}

const getContactInformationBlock = (view: View): ViewAccountBlockProps => {
    return {
        title: t('profile.contactInformation.title'),
        items: [
            {
                key: t('profile.contactInformation.firstName'),
                value: view.state.portal.customer?.firstName || '-',
            },
            {
                key: t('profile.contactInformation.lastName'),
                value: view.state.portal.customer?.lastName || '-',
            },
            {
                key: t('profile.contactInformation.email'),
                value: view.state.portal.customer?.email || '-',
            },
            {
                key: t('profile.contactInformation.phone'),
                value: view.state.portal.customer?.phone || '-',
            },
        ],
    }
}

const getAddressBlock = (view: View): ViewAccountBlockProps => {
    // Initialize local state for address fields if not already set
    if (!view.state.addressForm) {
        view.state.addressForm = {
            postalCode: view.state.portal.customer?.postalCode || '',
            address: view.state.portal.customer?.address || '',
            city: view.state.portal.customer?.city || '',
            errors: {
                postalCode: false,
                address: false,
                city: false,
            },
        }
    }

    const validateAddressFields = (): boolean => {
        const errors = {
            postalCode: !view.state.addressForm?.postalCode,
            address: !view.state.addressForm?.address,
            city: !view.state.addressForm?.city,
        }

        if (view.state.addressForm) {
            view.state.addressForm.errors = errors
        }
        view.update()

        return !errors.postalCode && !errors.address && !errors.city
    }

    // Check if there are any changes to the address fields
    const hasAddressChanges = (): boolean => {
        if (!view.state.addressForm || !view.state.portal.customer) {
            return false
        }

        return (
            view.state.addressForm.postalCode !== (view.state.portal.customer.postalCode || '') ||
            view.state.addressForm.address !== (view.state.portal.customer.address || '') ||
            view.state.addressForm.city !== (view.state.portal.customer.city || '')
        )
    }

    const saveAddress = () => {
        if (validateAddressFields() && view.state.addressForm) {
            view.api.portal
                .updateProfile({
                    postalCode: view.state.addressForm.postalCode,
                    address: view.state.addressForm.address,
                    city: view.state.addressForm.city,
                })
                .catch(() => window.location.reload())
        }
    }

    return {
        title: t('profile.address.title'),
        items: [
            {
                key: t('profile.address.postalCode'),
                value: view.state.addressForm?.postalCode || '',
                isEditable: true,
                onEdit: (value) => {
                    if (view.state.addressForm) {
                        view.state.addressForm.postalCode = value
                        view.state.addressForm.errors.postalCode = false
                    }
                    view.update()
                },
                error: view.state.addressForm?.errors.postalCode ? t('general.errors.required') : undefined,
            },
            {
                key: t('profile.address.address'),
                value: view.state.addressForm?.address || '',
                isEditable: true,
                onEdit: (value) => {
                    if (view.state.addressForm) {
                        view.state.addressForm.address = value
                        view.state.addressForm.errors.address = false
                    }
                    view.update()
                },
                error: view.state.addressForm?.errors.address ? t('general.errors.required') : undefined,
            },
            {
                key: t('profile.address.city'),
                value: view.state.addressForm?.city || '',
                isEditable: true,
                onEdit: (value) => {
                    if (view.state.addressForm) {
                        view.state.addressForm.city = value
                        view.state.addressForm.errors.city = false
                    }
                    view.update()
                },
                error: view.state.addressForm?.errors.city ? t('general.errors.required') : undefined,
            },
        ],
        button: hasAddressChanges()
            ? {
                  text: t('profile.address.saveButton'),
                  onClick: saveAddress,
                  variant: 'primary',
              }
            : undefined,
    }
}

const getNotificationPreferencesBlock = (view: View): ViewAccountBlockProps => {
    return {
        title: t('profile.notificationPreferences.title'),
        toggles: [
            {
                title: t('profile.notificationPreferences.marketingEmails'),
                toggle: {
                    id: 'marketing-emails',
                    label: t('profile.notificationPreferences.marketingEmailsLabel'),
                    checked: !!view.state.portal.customer?.marketingTerms,
                    onChange: (value) => {
                        view.api.portal
                            .updateProfile({
                                marketingConsent: value,
                            })
                            .catch(() => window.location.reload())
                    },
                },
                modal: {
                    trigger: t('general.readMore'),
                    modal: {
                        customCloseButton: {
                            text: t('general.close'),
                            variant: 'secondary',
                            isBlock: true,
                        },
                        children: (
                            <>
                                <ModalHeader>
                                    <h1> {t('profile.notificationPreferences.marketingModal.title')} </h1>
                                </ModalHeader>
                                <ModalContent>
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: t('profile.notificationPreferences.marketingModal.content'),
                                        }}
                                    />
                                </ModalContent>
                            </>
                        ),
                    },
                },
            },
        ],
    }
}

const getContactInformationDisclaimerBlock = (_view: View): ViewAccountBlockProps => {
    return {
        title: t('profile.contactInformationDisclaimer.title'),
        card: {
            title: t('profile.contactInformationDisclaimer.card.title'),
            description: t('profile.contactInformationDisclaimer.card.description'),
            link: t('profile.contactInformationDisclaimer.card.linkUrl'),
            linkText: t('profile.contactInformationDisclaimer.card.link'),
        },
    }
}

const getBillingInformationBlock = (view: View): ViewAccountBlockProps => {
    const { billing } = view.state.portal

    const block: ViewAccountBlockProps = {
        title: t('profile.billingInformation.title'),
        button: {
            text: t('profile.billingInformation.updatePaymentMethod'),
            onClick: () => void view.api.portal.updatePaymentMethod(),
        },
    }

    if (billing) {
        // mitExpiryMonth is in YYYY-MM format
        const [expYear, expMonth] = billing.mitExpiryMonth.split('-')

        block.items = [
            {
                key: t('profile.billingInformation.card'),
                value: billing.mitTokenName,
            },
            {
                key: t('profile.billingInformation.expires'),
                value: `${expMonth} / ${expYear}`,
                isAlerted: isPaymentMethodExpiring(view.state),
            },
        ]
    } else {
        block.card = {
            title: t('profile.billingInformation.noInfo'),
        }
    }

    return block
}

const getAccountProps = (view: View, accountView: PortalAccountView): ViewAccountProps => ({
    title: {
        title: t('profile.title'),
        button: {
            onClick: view.back,
        },
    },
    menu: {
        items: [
            {
                icon: ContactIcon,
                label: t('profile.contactInformation.title'),
                isCurrent: accountView === 'contact',
                url: '#/account/contact',
            },
            {
                icon: WalletIcon,
                label: t('profile.billingInformation.title'),
                isCurrent: accountView === 'billing',
                url: '#/account/billing',
            },
        ],
    },
    blocks: getBlocks(view, accountView),
})

const getBlocks = (view: View, accountView: PortalAccountView): ViewAccountBlockProps[] => {
    switch (accountView) {
        case 'contact':
            return [
                getContactInformationBlock(view),
                getAddressBlock(view),
                getNotificationPreferencesBlock(view),
                getContactInformationDisclaimerBlock(view),
            ]

        case 'billing':
            return [getBillingInformationBlock(view)]
    }
}

const getFaqProps = (view: View): ViewFaqProps => {
    return {
        title: {
            title: t('faq.title'),
            button: {
                onClick: view.back,
            },
        },
        items: t('faq.items', { returnObjects: true }) as AccordionItemProps[],
    }
}

const getOffersProps = (view: View): ViewOffersProps => {
    return {
        title: {
            title: t('offers.title'),
            button: {
                onClick: view.back,
            },
        },
        esisTag: {
            context: 'samsung-flex-portal',
            content: 'offers',
            customerUuid: view.state.portal.customer?.uuid || '',
            language: formatLocaleForEsisTag(view.state.portal.customer?.localeCode || ''),
        },
        notificationBlock: {
            ...getNotificationPreferencesBlock(view),
            title: undefined,
        },
    }
}

const getUpgradeProps = (view: View): ViewUpgradeProps => {
    return {
        title: {
            title: t('upgrade.title'),
            button: {
                onClick: view.back,
            },
        },
        esisTag: {
            context: 'samsung-flex-portal',
            content: 'upgrade',
            customerUuid: view.state.portal.customer?.uuid || '',
            language: formatLocaleForEsisTag(view.state.portal.customer?.localeCode || ''),
        },
    }
}

export function getPortalRootProps(route: Route, view: View, props: RootProps): RootProps {
    const { customer, subscriptions } = view.state.portal
    const subscriptionUuid = 'subscriptionUuid' in route ? route.subscriptionUuid : undefined
    const singleSubscription = subscriptions?.find((subscription) => subscription.uuid === subscriptionUuid)
    const hasSingleSubscription = subscriptions?.length && singleSubscription && !singleSubscription.hasEnded
    let portalProps: PortalProps = {}
    let headerLink: HeaderLink = 'dashboard'

    if (customer) {
        portalProps.visualLocaleCode = customer.visualLocaleCode

        switch (route.view) {
            case PortalViewPaths.MY_PREVIOUS_DEVICES:
            case PortalViewPaths.MY_SUBSCRIPTIONS:
                headerLink = 'subscriptions'

                if (hasSingleSubscription && route.step) {
                    switch (route.step) {
                        case 'manage':
                            portalProps.singleSubscriptionProps = getSingleSubscriptionsProps(singleSubscription, view)
                            break
                        case 'cancel':
                            portalProps.singleSubscriptionCancelProps = getSingleSubscriptionCancelProps(
                                singleSubscription,
                                view,
                            )
                            break
                        case 'swap':
                            portalProps = {
                                ...portalProps,
                                ...getSingleSubscriptionClaimProps(
                                    singleSubscription,
                                    route.parts?.length === 4 ? route.parts[3] : '',
                                    view,
                                ),
                            }
                            break
                        case 'swap-summary':
                            if (!view.state.portal.subscriptionClaim.damageType) {
                                window.location.hash = `${window.location.hash.replace('/swap-summary', '/swap')}`
                                view.update()
                                return {}
                            }

                            portalProps.singleSubscriptionSwapSummaryProps = getSingleSubscriptionSwapSummeryProps(
                                singleSubscription,
                                view,
                            )
                            break
                        case 'swap-receipt':
                            portalProps.singleSubscriptionSwapReceiptProps = getSingleSubscriptionSwapReceiptProps(
                                singleSubscription,
                                view,
                            )
                            break
                    }
                } else {
                    portalProps.mySubscriptionProps = getMySubscriptionsProps(
                        view,
                        route.subscriptionUuid,
                        route.view === PortalViewPaths.MY_PREVIOUS_DEVICES,
                    )
                }
                break

            case PortalViewPaths.INVOICES:
                headerLink = 'invoices'
                portalProps.invoicesProps = getInvoicesProps(view)
                break

            case PortalViewPaths.ACCOUNT:
                headerLink = 'account'
                portalProps.accountProps = getAccountProps(view, route.accountView)
                break

            case PortalViewPaths.FAQ:
                headerLink = 'faq'
                portalProps.faqProps = getFaqProps(view)
                break

            case PortalViewPaths.OFFERS:
                if (customer.hasEsisOffers) {
                    headerLink = 'offers'
                    portalProps.offersProps = getOffersProps(view)
                }
                break

            case PortalViewPaths.UPGRADE:
                if (customer.hasEsisOffers) {
                    portalProps.upgradeProps = getUpgradeProps(view)
                }
                break

            default:
            case PortalViewPaths.DASHBOARD:
                portalProps.dashboardProps = getDashboardProps(view, customer)
                break
        }

        const notifications: NotificationProps[] = props.notifications?.items || []
        if (customer.status === CustomerStatus.FRAUDULENT) {
            notifications.push({
                children: t('portal.fraudulentCustomer'),
                isDefaultOpen: true,
                type: 'error',
            })
        }

        if (customer.status === CustomerStatus.PAYINGISSUES) {
            notifications.push({
                children: t('portal.payingIssuesCustomer'),
                isDefaultOpen: true,
                type: 'error',
            })
        }

        return {
            portalHeader: getCustomerHeader(view, route, headerLink),
            portal: portalProps,
            notifications: {
                items: notifications,
            },
        }
    }

    switch (route.view) {
        case PortalViewPaths.DEBUG:
            return getDebugProps(view)
        case PortalViewPaths.LOGIN:
            return getLoginProps(view)
        default:
            return getLanguageSelectProps(view)
    }
}

export const Portal = (props: PortalProps): JSX.Element => {
    return (
        <>
            {props.loginProps && <Login {...props.loginProps} />}
            {props.dashboardProps && <Dashboard {...props.dashboardProps} />}
            {props.mySubscriptionProps && <MySubscriptions {...props.mySubscriptionProps} />}
            {props.singleSubscriptionProps && <SingleSubscription {...props.singleSubscriptionProps} />}
            {props.invoicesProps && <Invoices {...props.invoicesProps} />}
            {props.languageProps && <Languages {...props.languageProps} />}
            {props.accountProps && <ViewAccount {...props.accountProps} />}
            {props.faqProps && <ViewFaq {...props.faqProps} />}
            {props.offersProps && <ViewOffers {...props.offersProps} />}
            {props.upgradeProps && <ViewUpgrade {...props.upgradeProps} />}
            {props.singleSubscriptionCancelProps && <ViewCancel {...props.singleSubscriptionCancelProps} />}
            {props.singleSubscriptionSwapProps && <ViewSwap {...props.singleSubscriptionSwapProps} />}
            {props.singleSubscriptionSwapSummaryProps && <ViewSwap {...props.singleSubscriptionSwapSummaryProps} />}
            {props.singleSubscriptionSwapReceiptProps && (
                <NotificationPage {...props.singleSubscriptionSwapReceiptProps} />
            )}
            {props.debugProps && <Debug {...props.debugProps} />}
        </>
    )
}
